import axios from "axios";

const API_BASE_URL =
  process.env.REACT_APP_API_URL || "http://localhost:5000/api";

const api = axios.create({
  baseURL: API_BASE_URL,
  withCredentials: true,
});

// Redirect to login when session expires or is invalidated
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      const requestUrl = error.config?.url || "";
      const alreadyOnLogin = window.location.pathname === "/anmelden";
      // Don't redirect for the session-check call itself or when already on the login page
      if (!alreadyOnLogin && !requestUrl.includes("/auth/me")) {
        window.location.href = "/anmelden";
      }
    }
    return Promise.reject(error);
  },
);

export const activitiesAPI = {
  getAll: (filters) => {
    return api.get("/activities", { params: filters });
  },
  // Vollständiger Datensatz – die Admin-Listen laden nur Anzeigefelder,
  // das Bearbeitungsformular braucht aber alle Felder.
  getById: (id) => api.get(`/activities/${id}`),
  importOpeningHours: (id) =>
    api.post(`/activities/${id}/opening-hours/import`),
  importOpeningHoursFromWebsite: (name, address, website) =>
    api.post("/activities/opening-hours/import-from-website", {
      name,
      address,
      website,
    }),
  create: (data) => {
    // Wenn data ein FormData-Objekt ist, setze den richtigen Content-Type
    const config =
      data instanceof FormData
        ? {
            headers: {
              "Content-Type": "multipart/form-data",
            },
          }
        : {};
    return api.post("/activities", data, config);
  },
  update: (id, data) => {
    const config =
      data instanceof FormData
        ? {
            headers: {
              "Content-Type": "multipart/form-data",
            },
          }
        : {};
    return api.put(`/activities/${id}`, data, config);
  },
  delete: (id) => api.delete(`/activities/${id}`),
  // Kopie einer Aktivität ('<Name> - Kopie'); die Kopie ist zunächst 'pending'
  duplicate: (id) => api.post(`/activities/${id}/duplicate`),
};

export const authAPI = {
  login: (credentials) => api.post("/auth/login", credentials),
  register: (userData) => api.post("/auth/register", userData),
  logout: () => api.post("/auth/logout"),
  getCurrentUser: () => api.get("/auth/me"),
};

export const usersAPI = {
  getAll: () => api.get("/users"),
  updateRole: (id, data) => api.put(`/users/${id}/role`, data),
  delete: (id) => api.delete(`/users/${id}`),
  geocode: (address) => api.get("/users/geocode", { params: { address } }),
};

export const moderationAPI = {
  // filters: { status, q, location_name, location_name_exact, address,
  //            lat, lng, radius_m, sort, fields, limit, offset }
  // Mit `limit` antwortet die API als { items, total, offset, limit, counts },
  // ohne `limit` als flache Liste.
  getAll: (filters) => api.get("/moderation/activities", { params: filters }),
  // Vorhandene Ortsbezeichnungen (Autovervollständigung), optional per prefix
  getLocationNames: (params) =>
    api.get("/moderation/locations", { params }),
  approve: (id) => api.post(`/moderation/activities/${id}/approve`),
  reject: (id, reason) =>
    api.post(`/moderation/activities/${id}/reject`, { reason }),
};

export const sourcesAPI = {
  // Quellen-Registry: Filter = { region, source_type, permission_status }
  getAll: (filters) => api.get("/sources", { params: filters }),
  get: (id) => api.get(`/sources/${id}`),
  create: (data) => api.post("/sources", data),
  update: (id, data) => api.put(`/sources/${id}`, data),
  delete: (id) => api.delete(`/sources/${id}`),
  // Import aus bestehenden Aktivitäten (Vorschau + Bestätigung).
  // Vorschau kann dauern (externe Geocoding-Abfragen) -> grosszügiger Timeout.
  importPreview: () => api.get("/sources/import-preview", { timeout: 120000 }),
  importConfirm: (candidates) =>
    api.post("/sources/import-confirm", { candidates }),
};

export default api;
